import React from 'react';

import { GlobalOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { Handle, Position } from 'reactflow';

import { ENTITY_TYPES } from '@/shared/api/openmetadata';
import type { LineageNodeData } from '@/shared/types/lineageNode';
import { ServiceIcon } from '@/shared/ui/ServiceIcon';

import styles from './LineageNode.module.css';

type LineageNodeProps = {
  data: LineageNodeData;
  selected: boolean;
  onSelect: (data: LineageNodeData) => void;
};

export const LineageNode: React.FC<LineageNodeProps> = ({ data, selected, onSelect }) => {
  const hierarchyPath = [data.service, data.database, data.schema].filter(Boolean).join(' / ');

  return (
    <>
      <div
        className={classNames(styles.lineageNode, { [styles.selected]: selected })}
        onClick={() => onSelect(data)}
        role="button"
        tabIndex={0}>
        <Handle type="target" position={Position.Left} className={styles.nodeHandle} data-handlepos="left" />
        <Handle type="source" position={Position.Right} className={styles.nodeHandle} data-handlepos="right" />

        {hierarchyPath && <div className={styles.hierarchy}>{hierarchyPath}</div>}

        <div className={styles.header}>
          <div className={styles.headerLeft}>
            {data.entityType === ENTITY_TYPES.DOMAIN ? (
              <GlobalOutlined style={{ fontSize: 32, color: 'var(--opds-glo-palette-gray-30)' }} />
            ) : (
              <ServiceIcon serviceType={data.serviceType} size={36} />
            )}
          </div>
          <div className={styles.titleContainer}>
            <span className={styles.label}>{data.label}</span>
            <span className={styles.title}>{data.displayName}</span>
          </div>
          <div className={styles.headerRight}></div>
        </div>

        {data.showColumns && data.columns && data.columns.length > 0 && (
          <div className={styles.columnsContainer}>
            {data.columns.map((column) => (
              <div key={column.name} className={styles.columnRow}>
                <Handle
                  type="target"
                  position={Position.Left}
                  id={`${data.fullyQualifiedName}-${column.name}-target`}
                  className={styles.columnHandle}
                />
                <span className={styles.columnName}>{column.name}</span>
                <span className={styles.columnType}>{column.dataType}</span>
                <Handle
                  type="source"
                  position={Position.Right}
                  id={`${data.fullyQualifiedName}-${column.name}-source`}
                  className={styles.columnHandle}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};
